import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import isEqual from 'lodash/isEqual';
import React from 'react';
import {useDrag, useDrop} from 'react-dnd';

import {ApiFeature} from 'app/modules/Remote/Feature';
import {useUserInfo} from 'app/providers/AuthProvider';
import {isOwnerOrAdmin} from 'app/utils/userUtils';

import cs from './PropertyOverview.module.scss';
import {
  getDefaultPropertyOverviewColumnSettings,
  getPropertyOverviewDefaultColumnMetadata,
  makeCustomColumn,
} from './utils';
export interface PropertyTableRow {
  features: I.ImmutableOf<ApiFeature[]>;
  subRows?: PropertyTableRow[];
}

export interface OrderedVisibleColumnConfiguration {
  orderedVisibleColumns: string[];
}

interface DragItem {
  type: 'column';
  id: string;
}

const HEADER_TEXT_OVERRIDES = {
  imagery: 'High-Res Imagery',
  userNoteCount: 'Notes',
};

/**
 * popover that controls column visibility and order.
 */
export const ColumnSettingsContent: React.FunctionComponent<
  React.PropsWithChildren<{
    columnSettings: OrderedVisibleColumnConfiguration;
    onColumnSettingsChange: (settings: OrderedVisibleColumnConfiguration) => void;
    saveColumnSettings: (
      orgId: string,
      columnSettings: OrderedVisibleColumnConfiguration
    ) => Promise<void>;
    hideMenuItems?: boolean;
    additionalFeatureProperties: I.Set<string>;
  }>
> = ({
  columnSettings,
  onColumnSettingsChange,
  saveColumnSettings,
  hideMenuItems,
  additionalFeatureProperties,
}) => {
  // we don't want to even display the selection checkbox column as an option
  const [organization, profile] = useUserInfo();
  const canSaveForAllPortfolios = isOwnerOrAdmin(profile.get('role'));

  const defaultColumns = getPropertyOverviewDefaultColumnMetadata(organization);

  // these are all the columns to display in the popover; pin name to the top.
  const nameCol = defaultColumns.find((col) => col.id === 'name');
  const configurableCols = defaultColumns.filter((col) => col.id !== 'name');
  const allColumnIds: string[] = [nameCol!.id!, ...configurableCols.map((col) => col.id!)];

  const moveIdToIdx = (id: string, idx: number) => {
    if (idx > columnSettings.orderedVisibleColumns.length) {
      return;
    }

    const updated = columnSettings.orderedVisibleColumns.filter((i) => i !== id);
    updated.splice(idx, 0, id);

    onColumnSettingsChange({
      orderedVisibleColumns: updated,
    });
  };

  const [, drop] = useDrop({accept: 'column'});

  const toggleColumnVisibility = (columnId: string) => {
    const currentIndex = columnSettings.orderedVisibleColumns.indexOf(columnId);
    let updated;

    if (currentIndex >= 0) {
      // Hide: remove from array
      updated = columnSettings.orderedVisibleColumns.filter((id) => id !== columnId);
    } else {
      // Show: add to end of array
      updated = [...columnSettings.orderedVisibleColumns, columnId];
    }

    onColumnSettingsChange({
      orderedVisibleColumns: updated,
    });
  };

  return (
    <div ref={drop} className={cs.columnSettingsPopover}>
      <B.Menu>
        <B.MenuDivider title="Columns" />
        {/* name column*/}
        <ColumnSettingsRow
          key="name"
          columnId="name"
          headerText={'Name'}
          isVisible={true}
          currentIdx={0}
          moveIdToIdx={() => {
            //static column no need to move
          }}
          onToggleVisibility={() => {
            //don't allow toggling the name column
          }}
        />
        {/* all visible columns */}
        {columnSettings.orderedVisibleColumns.map((columnId, idx) => {
          const column =
            defaultColumns.find((col) => col.id === columnId) || makeCustomColumn(columnId);

          //drop column if we don't have an id (should be impossible), can't find a column (may happen with custom columns
          //if different portfolios don't have access to the same additionalFeatureProperties), OR in case the custom
          //columns include one of our hardcoded columns (name or selection)
          if (!column || !columnId || ['name', 'selection'].includes(columnId)) return null;

          const headerText = HEADER_TEXT_OVERRIDES[columnId] || String(column.Header);

          return (
            <ColumnSettingsRow
              key={columnId}
              columnId={columnId}
              headerText={headerText}
              isVisible={true}
              currentIdx={idx}
              moveIdToIdx={moveIdToIdx}
              onToggleVisibility={toggleColumnVisibility}
              warningMessage={
                !additionalFeatureProperties?.has(columnId) &&
                !defaultColumns.find((col) => col.id === columnId)
                  ? hideMenuItems
                    ? 'This is a custom column.'
                    : 'This column is not available in this portfolio.'
                  : undefined
              }
            />
          );
        })}
        {/* all hidden columns at the end, so they can still be toggled back on */}
        {allColumnIds
          .filter(
            (id) =>
              id !== 'name' &&
              id !== 'selection' &&
              !columnSettings.orderedVisibleColumns.includes(id)
          )
          .map((columnId, idx) => {
            const column = defaultColumns.find((col) => col.id === columnId);
            if (!column || !columnId) return null;
            const headerText = HEADER_TEXT_OVERRIDES[columnId] || String(column.Header);
            return (
              <ColumnSettingsRow
                key={columnId}
                columnId={columnId}
                headerText={headerText}
                isVisible={columnSettings.orderedVisibleColumns.length === 0}
                currentIdx={columnSettings.orderedVisibleColumns.length + idx}
                moveIdToIdx={(id, idx) =>
                  moveIdToIdx(id, idx + columnSettings.orderedVisibleColumns.length)
                }
                onToggleVisibility={toggleColumnVisibility}
              />
            );
          })}

        {!hideMenuItems && (
          <>
            {additionalFeatureProperties?.size > 0 && (
              <B.MenuItem
                text="Add additional attribute from file"
                popoverProps={{popoverClassName: cs.additionalAttributesPopover}}
                icon={
                  <B.Icon
                    icon={additionalFeatureProperties?.size > 200 ? 'warning-sign' : undefined}
                    htmlTitle={
                      additionalFeatureProperties?.size > 200
                        ? 'Only the first 200 attributes will be shown'
                        : undefined
                    }
                  />
                }
              >
                {additionalFeatureProperties
                  ?.toArray()
                  // safeguard in place in case someone manages to upload a messed up CSV or something
                  // if anyone actually needs more than 200 attributes in Lens we will address this then
                  .slice(0, 200)
                  .map((property) => (
                    <B.MenuItem
                      key={property}
                      text={property}
                      onClick={() => {
                        if (columnSettings.orderedVisibleColumns.includes(property)) {
                          return;
                        }
                        onColumnSettingsChange({
                          orderedVisibleColumns: [
                            ...columnSettings.orderedVisibleColumns,
                            property,
                          ],
                        });
                      }}
                      shouldDismissPopover={false}
                      disabled={columnSettings.orderedVisibleColumns.includes(property)}
                    />
                  ))}
              </B.MenuItem>
            )}
            <B.MenuDivider />
            <B.MenuItem
              text="Reset to organization default"
              disabled={isEqual(
                columnSettings,
                getDefaultPropertyOverviewColumnSettings(organization)
              )}
              onClick={() => {
                onColumnSettingsChange(getDefaultPropertyOverviewColumnSettings(organization));
              }}
            />
            <B.Tooltip
              content={'Only organization admins can update this setting.'}
              disabled={canSaveForAllPortfolios}
            >
              <B.MenuItem
                text="Save for all portfolios"
                intent={B.Intent.PRIMARY}
                shouldDismissPopover={false}
                disabled={!canSaveForAllPortfolios}
                onClick={() => {
                  if (
                    confirm(
                      'This will save these column settings for all portfolios in your organization. Are you sure?'
                    )
                  ) {
                    saveColumnSettings(organization.get('id'), columnSettings);
                  }
                }}
              ></B.MenuItem>
            </B.Tooltip>
          </>
        )}
      </B.Menu>
    </div>
  );
};

const ColumnSettingsRow: React.FunctionComponent<
  React.PropsWithChildren<{
    columnId: string;
    headerText: string;
    isVisible: boolean;
    currentIdx: number;
    moveIdToIdx: (id: string, idx: number) => void;
    onToggleVisibility: (id: string) => void;
    warningMessage?: string;
  }>
> = ({
  columnId,
  headerText,
  isVisible,
  currentIdx,
  moveIdToIdx,
  onToggleVisibility,
  warningMessage,
}) => {
  const item: DragItem = {type: 'column', id: columnId};
  const isName = columnId === 'name';

  const [{isDragging}, drag] = useDrag(
    () => ({
      type: 'column',
      item: item,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: !isName && isVisible,
    }),
    [columnId, isName, currentIdx]
  );

  const [, drop] = useDrop({
    accept: 'column',
    canDrop: () => false,
    hover({id: draggedId}: DragItem) {
      if (draggedId !== columnId) {
        moveIdToIdx(draggedId, currentIdx);
      }
    },
  });

  return (
    <div
      ref={(node) => {
        drag(node);
        drop(node);
      }}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: isName || !isVisible ? 'default' : 'grab',
        display: 'flex',
        alignItems: 'center',
        padding: '0.5rem',
      }}
    >
      <B.Checkbox
        checked={isVisible}
        disabled={isName}
        onChange={() => {
          if (warningMessage) {
            confirm(
              warningMessage +
                " If removed, it can only be re-added from its portfolio's Property Overview table. Do you still want to remove it?"
            )
              ? onToggleVisibility(columnId)
              : null;
          } else {
            onToggleVisibility(columnId);
          }
        }}
      />
      <span style={{flex: 1}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          {headerText}
          {warningMessage && (
            <B.Tooltip content={warningMessage}>
              <B.Icon icon="warning-sign" intent={B.Intent.WARNING} />
            </B.Tooltip>
          )}
        </div>
      </span>
      {!isName && isVisible && <B.Icon icon="drag-handle-vertical" style={{opacity: 0.5}} />}
    </div>
  );
};
